import styles from "./edittaskform.module.css";
import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { AddLogCallAPI } from "../../../components/AddLogs";
import { toast } from 'react-smart-toaster';
import FrequencySelector from "./FrequencySelector";
import TaskQuestions from "./TaskQuestions";
import { TableLoaderSmall } from "../../../components/LoaderC";

const taskSchema = {
    task_name: yup.string().required("This field is Required"),
};

const schema = yup.object().shape({
    title: yup.string().required("This field is Required"),
    description: yup.string(),
    property_id: yup.string(),
    technician: yup.string().required("This field is Required"),
    secondary_technician: yup.string(),
    taskList: yup.array().of(yup.object().shape(taskSchema)),
});

const EditTaskForm = ({ setEditTaskModalOpen, selectedId, setLoading, setSnackbarOpen, setSnackbarMsg, editTaskLocal }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [contentLoading, setContentLoading] = useState(true);
    const [propertyList, setPropertyList] = useState([]);
    const [allTechnicianList, setAllTechnicianList] = useState([]);
    const [technicianList, setTechnicianList] = useState([]);
    const [secondaryTechnicianList, setSecondaryTechnicianList] = useState([]);
    const [charsLeft, setCharsLeft] = useState({ description: 200 });
    const [taskDetails, setTaskDetails] = useState(null);
    const [frequency, setFrequency] = useState('Daily');
    const [selectedDays, setSelectedDays] = useState([]);
    const [selectedWeeks, setSelectedWeeks] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [prevTaskList, setPrevTaskList] = useState([]);

    const { handleSubmit, control, errors, setValue } = useForm({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            title: '',
            description: '',
            property_id: '',
            technician: '',
            secondary_technician: ''
        },
        shouldUnregister: false
    });

    const { fields, append, remove } = useFieldArray({ control, name: "taskList", shouldUnregister: false });

    useEffect(() => {
        async function fetchPropertyList() {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            axios(process.env.REACT_APP_CASE_API_URL + "v1/property-list", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }).then((response) => {
                setPropertyList(response.data.data);
            }).catch(() => { });
        }
        fetchPropertyList();
    }, [getAccessTokenSilently]);

    useEffect(() => {
        async function fetchPropertyList() {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            axios(process.env.REACT_APP_CASE_API_URL + "v1/all-user-list", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }).then((response) => {
                setAllTechnicianList(response.data.data);
            }).catch(() => { });
        }
        fetchPropertyList();
    }, [getAccessTokenSilently]);

    useEffect(() => {
        async function fetchTaskDetails() {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            axios(process.env.REACT_APP_CASE_API_URL + "v1/tasks/" + selectedId, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }).then((response) => {
                setContentLoading(false);
                setTaskDetails(response.data.data);
            }).catch(() => {
                setContentLoading(false);
            });
        }
        if (selectedId)
            fetchTaskDetails();
    }, [selectedId, getAccessTokenSilently]);

    useEffect(() => {
        if (taskDetails) {
            setValue('title', taskDetails?.title);
            setValue('description', taskDetails?.description);
            setValue('property_id', taskDetails?.property_id);
            setValue('description', taskDetails?.description ? taskDetails?.description : '');

            setFrequency(taskDetails?.frequency);

            if (taskDetails?.frequency === 'Daily') {
                setSelectedDays(taskDetails?.days);
            }

            if (taskDetails?.frequency === 'Weekly') {
                setSelectedWeeks(taskDetails?.weeks.map(Number));
                setSelectedDays(taskDetails?.days);
            }

            setPrevTaskList(taskDetails?.task_list);

            if (taskDetails?.description) {
                setCharsLeft((prev) => {
                    return { ...prev, description: 200 - taskDetails?.description?.length };
                });
            }

            if (taskDetails?.property_id) {
                let technicianListTemp = allTechnicianList?.filter(i => i?.properties?.indexOf(taskDetails?.property_id) > -1);
                setTechnicianList(technicianListTemp);

                if (taskDetails?.technician) {
                    let secondaryTechnicianTemp = technicianListTemp?.filter(i => i?.id !== parseInt(taskDetails?.technician));

                    setSecondaryTechnicianList(secondaryTechnicianTemp);
                }

                setValue('technician', taskDetails?.technician);

                if (taskDetails?.secondary_technician) {
                    setValue('secondary_technician', taskDetails?.secondary_technician);
                }
            }
        }
    }, [taskDetails, allTechnicianList, setValue]);

    useEffect(() => {
        if (taskDetails) {
            if (taskDetails?.task_list?.length) {

                let taskData = taskDetails?.task_list?.map(i => {
                    return { task_id: i?.id, task_name: i?.task_name, question: i?.question, question_type: i?.question_type, is_required: i?.is_required, answer_options: i?.answer_options }
                });

                setValue('taskList', taskData);
            }
        }
    }, [taskDetails]);

    const onSubmit = async (data) => {
        data = { ...data, frequency: frequency };

        if (frequency === 'Daily') {
            if (selectedDays.length === 0) {
                toast.error('Please select day(s)');
                return false;
            } else {
                data = { ...data, days: selectedDays.join(',') };
            }
        }

        if (frequency === 'Weekly') {
            if (selectedWeeks.length === 0) {
                toast.error('Please select week(s)');
                return false;
            } else if (selectedDays.length === 0) {
                toast.error('Please select day(s)');
                return false;
            } else {
                data = { ...data, weeks: selectedWeeks.join(','), days: selectedDays.join(',') };
            }
        }

        setLoading(true);
        setEditTaskModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        return axios.post(process.env.REACT_APP_CASE_API_URL + "v1/tasks/" + taskDetails?.id, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            setSnackbarOpen(true);
            setSnackbarMsg(response.data.message);

            editTaskLocal(response.data.data);

            let logData = {
                'title': 'Task list has been updated',
                'description': [
                    'Name: ' + response?.data?.data?.title,
                    'Property: ' + response?.data?.data?.property_name,
                    'Technician: ' + response?.data?.data?.technician_name
                ]
            }
            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<form onSubmit={handleSubmit(onSubmit)}>
        {contentLoading && <div className="Loader TableLoader"><TableLoaderSmall /></div>}

        {!contentLoading && <div className={`${styles.AddprospectFirstStepForm}`}>
            <div className={`${styles.FormArea}`}>
                <div className={`${styles.AddPropertyHalfSecRow}`}>
                    <div className={`${styles.HalfSecFull} FormGroup`}>
                        <Controller
                            name={`title`}
                            control={control}
                            render={(field) => (<TextField
                                {...field}
                                id="outlined-basic"
                                label="Task name*"
                            />)}
                        />
                        {errors.title && (<p className={`${styles.ErrorM}`}>{errors?.title?.message}</p>)}
                    </div>
                    <div className={`${styles.HalfSecFull} FormGroup`}>
                        <Controller
                            name={`description`}
                            control={control}
                            render={(field) => (<TextField
                                {...field}
                                id="outlined-basic"
                                label="Description"
                                multiline
                                rows={4}
                                onChange={(e) => {
                                    field.onChange(e);

                                    setCharsLeft((prev) => {
                                        return { ...prev, description: 200 - e.target.value.length };
                                    });
                                }}
                                inputProps={{ maxLength: 200 }}
                            />)}
                        />
                        <p className={`${styles.Example}`}>{charsLeft?.description > 1 ? charsLeft?.description + " characters remaining" : charsLeft?.description + " character remaining"}</p>
                        {errors.description && (<p className={`${styles.ErrorM}`}>{errors?.description?.message}</p>)}
                    </div>
                </div>
                <div className={`${styles.HalfSecFull} FormGroup`}>
                    <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">Property</InputLabel>
                        <Controller
                            name="property_id"
                            control={control}
                            render={(field) => (
                                <Select
                                    {...field}
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Property"
                                    onChange={(e) => {
                                        field.onChange(e);

                                        let technicianListTemp = allTechnicianList?.filter(i => i?.properties?.indexOf(e?.target?.value) > -1);

                                        setTechnicianList(technicianListTemp);
                                        setSecondaryTechnicianList([]);
                                        setValue('technician', '');
                                    }}
                                >
                                    {propertyList?.map((item) => {
                                        return (<MenuItem value={item.value}>{item.label}</MenuItem>);
                                    })}
                                </Select>
                            )}
                        />
                    </FormControl>
                    {errors.property_id && (<p className={`${styles.ErrorM}`}>{errors?.property_id?.message}</p>)}
                </div>
                <div className={`${styles.HalfSecFull} FormGroup`}>
                    <Controller
                        name="technician"
                        control={control}
                        render={(field) => (<>
                            <Autocomplete
                                options={technicianList}
                                getOptionLabel={(option) => option?.name || ""}
                                value={technicianList.find((t) => t.id === field.value) || null}
                                onChange={(_, value) => {
                                    field.onChange(value ? value.id : "");

                                    const secondaryTechnicianTemp = technicianList.filter((i) => i.id !== value?.id);

                                    setSecondaryTechnicianList(secondaryTechnicianTemp);
                                    setValue("secondary_technician", "");
                                }}
                                renderInput={(params) => (<TextField {...params} label="Primary Technician*" />)}
                                fullWidth
                            />
                            <p style={{ fontSize: 12, color: "#666", margin: '4px 0' }}>This checklist can be accessed in the Management App by users who have permission.</p>
                        </>)}
                    />
                    {errors.technician && (<p className={`${styles.ErrorM}`}>{errors.technician.message}</p>)}
                </div>

                <div className={`${styles.HalfSecFull} FormGroup`}>
                    <Controller
                        name="secondary_technician"
                        control={control}
                        render={(field) => (<>
                            <Autocomplete
                                options={secondaryTechnicianList}
                                getOptionLabel={(option) => option?.name || ""}
                                value={secondaryTechnicianList.find((t) => t.id === field.value) || null}
                                onChange={(_, value) => { field.onChange(value ? value.id : ""); }}
                                renderInput={(params) => (<TextField                                    {...params} label="Secondary Technician" />)}
                                fullWidth
                            />
                            <p style={{ fontSize: 12, color: "#666", margin: '4px 0' }}>This checklist can be accessed in the Management App by users who have permission.</p>
                        </>)}
                    />
                    {errors.secondary_technician && (<p className={`${styles.ErrorM}`}>{errors.secondary_technician.message}</p>)}
                </div>

                <FrequencySelector frequency={frequency} setFrequency={setFrequency.bind(this)} selectedDays={selectedDays} setSelectedDays={setSelectedDays.bind(this)} selectedWeeks={selectedWeeks} setSelectedWeeks={setSelectedWeeks.bind(this)} selectedDates={selectedDates} setSelectedDates={setSelectedDates.bind(this)} />
            </div>
            <div className={`${styles.ChecklistArea}`}>
                <TaskQuestions fields={fields} append={append.bind(this)} remove={remove.bind(this)} control={control} errors={errors} setValue={setValue.bind(this)} prevTaskList={prevTaskList} />
            </div>
            <div className={`${styles.ButtonAction}`}>
                <Button onClick={(e) => setEditTaskModalOpen(false)} type="button" className="CancelPopupBU">Cancel</Button>
                <Button className="SubmitPopupBU" type="submit">Update</Button>
            </div>
        </div>}
    </form>)
}

export default EditTaskForm;